import { inject, TemplateRef, ViewContainerRef} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { fromEvent, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CellPreparedEvent } from 'devextreme/ui/data_grid';

export interface ItemsTableColumnFix {
  value: string | boolean;
  label: string;
}

export class ItemsTableHeaderPopupService {
  overlayRef: OverlayRef | null;
  overlay = inject(Overlay);
  overlaySubscription: Subscription;
  positions: ConnectedPosition[] = [ {
    originX: 'end', originY: 'bottom',
    overlayX: 'end', overlayY: 'top',
  }];
  columnFixOptions: ItemsTableColumnFix[] = [
    { value: 'left', label: 'FIX_TO_LEFT'},
    { value: 'right', label: 'FIX_TO_RIGHT'},
    { value: false, label: 'UNFIX'},
  ];

  open({ x, y }: MouseEvent, context: any, template: TemplateRef<any>, vcRef: ViewContainerRef): void {
    this.close();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo({ x, y })
      .withPositions(this.positions);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.overlayRef.attach(new TemplatePortal(template, vcRef, { $implicit: context }));
    this.setCloseSubscription();
  }

  private setCloseSubscription(): void {
    this.overlaySubscription = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          const clickTarget = event.target as HTMLElement;
          return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
        }),
        take(1)
      ).subscribe(() => this.close());
  }

  addFixColumnPopup(e: CellPreparedEvent, template: TemplateRef<any>, vcr: ViewContainerRef): void {
    const columnIndicators = e.cellElement.querySelector('.dx-column-indicators');
    if (columnIndicators && !columnIndicators.querySelector('.c-items-table-header-icon-freeze')) {
      const spanElement = document.createElement('span');
      spanElement.classList.add('c-items-table-header-icon-freeze');
      spanElement.style.visibility = 'hidden';
      spanElement.setAttribute('id', `c-items-table-header-icon-freeze__${e.column.dataField}`);
      const imgElement = document.createElement('img');
      imgElement.src = 'assets/images/icons-src/icon-column-freeze.svg';
      spanElement.appendChild(imgElement);

      const headerFilter = columnIndicators.querySelector('.dx-header-filter');
      if (headerFilter) {
        columnIndicators.insertBefore(spanElement, headerFilter);
      } else {
        columnIndicators.appendChild(spanElement);
      }
    }
    e.cellElement.onmouseenter = () => this.toggleVisibility(e.cellElement, true);
    e.cellElement.onmouseleave = () => this.toggleVisibility(e.cellElement, false);
  }

  private toggleVisibility = (cellElement: HTMLElement, flag: boolean) => {
    const spanElement: HTMLElement = cellElement.querySelector('.c-items-table-header-icon-freeze');
    if (spanElement) {
      spanElement.style.visibility = flag ? 'visible' : 'hidden';
    }
  }

  addAsterixIcon(e: CellPreparedEvent): void {
    const textContent = e.cellElement.querySelector('.dx-datagrid-text-content');
    if (textContent && !textContent.querySelector('.c-items-table-header-asterix')) {
      const spanElement = document.createElement('span');
      spanElement.classList.add('c-items-table-header-asterix');
      spanElement.innerHTML = '*';
      textContent.insertBefore(spanElement, textContent.firstChild);
    }
  }

  close(): void {
    this.overlaySubscription?.unsubscribe();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

}
